/**
 * Urgency Creators Section
 * Features: Touch/swipe support, autoplay, responsive slides
 * Clean, accessible slideshow functionality
 */

class UrgencyCreators {
  constructor() {
    this.sections = document.querySelectorAll('.urgency-creators');
    
    if (this.sections.length > 0) {
      this.init();
    }
  }

  init() {
    this.sections.forEach(section => {
      this.initSlideshow(section);
    });
  }

  /**
   * Initialize slideshow functionality
   */
  initSlideshow(section) {
    const slider = section.querySelector('.urgency-creators__slider');
    if (!slider) return;
    
    const track = slider.querySelector('.urgency-creators__track');
    const slides = track.querySelectorAll('.urgency-creators__slide');
    const prevBtn = slider.querySelector('.urgency-creators__arrow--prev');
    const nextBtn = slider.querySelector('.urgency-creators__arrow--next');
    const dotsContainer = slider.querySelector('.urgency-creators__dots');
    
    if (slides.length === 0) return;
    
    // Get responsive settings
    const slidesDesktop = parseInt(slider.dataset.slidesDesktop) || 6;
    const slidesTablet = parseInt(slider.dataset.slidesTablet) || 4;
    const slidesMobile = parseInt(slider.dataset.slidesMobile) || 2;
    const autoplay = slider.dataset.autoplay === 'true';
    const autoplaySpeed = parseInt(slider.dataset.autoplaySpeed) || 3000;
    
    let currentSlide = 0;
    let slidesPerView = this.getSlidesPerView(slidesDesktop, slidesTablet, slidesMobile);
    let autoplayInterval = null;
    
    // Create dots if enabled
    if (dotsContainer && slides.length > slidesPerView) {
      this.createDots(dotsContainer, slides.length, slidesPerView);
    }
    
    // Store goToSlide for dots
    const goToSlide = (index) => {
      const maxSlide = Math.max(0, slides.length - slidesPerView);
      currentSlide = Math.max(0, Math.min(index, maxSlide));
      updateSlider();
    };
    this.goToSlide = goToSlide;
    
    // Update arrow states function
    const updateArrows = () => {
      const maxSlide = Math.max(0, slides.length - slidesPerView);
      if (prevBtn) {
        prevBtn.style.opacity = currentSlide > 0 ? '1' : '0.5';
        prevBtn.style.pointerEvents = currentSlide > 0 ? 'auto' : 'none';
      }
      if (nextBtn) {
        nextBtn.style.opacity = currentSlide < maxSlide ? '1' : '0.5';
        nextBtn.style.pointerEvents = currentSlide < maxSlide ? 'auto' : 'none';
      }
    };
    
    // Update slider layout
    const updateSlider = () => {
      slidesPerView = this.getSlidesPerView(slidesDesktop, slidesTablet, slidesMobile);
      const slideWidth = 100 / slidesPerView;
      
      slides.forEach((slide, index) => {
        slide.style.flex = `0 0 ${slideWidth}%`;
      });
      
      const maxSlide = Math.max(0, slides.length - slidesPerView);
      currentSlide = Math.min(currentSlide, maxSlide);
      
      this.updateSliderPosition(track, currentSlide, slideWidth);
      this.updateDots(dotsContainer, currentSlide, slidesPerView);
      updateArrows();
    };
    
    // Navigation functions
    const nextSlide = () => {
      const maxSlide = Math.max(0, slides.length - slidesPerView);
      if (currentSlide < maxSlide) {
        currentSlide++;
      } else {
        currentSlide = 0; // Loop back to start
      }
      updateSlider();
    };
    
    const prevSlide = () => {
      const maxSlide = Math.max(0, slides.length - slidesPerView);
      if (currentSlide > 0) {
        currentSlide--;
      } else {
        currentSlide = maxSlide; // Loop to end
      }
      updateSlider();
    };
    
    // Event listeners for arrows
    if (nextBtn) {
      nextBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        nextSlide();
      });
    }
    
    if (prevBtn) {
      prevBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        prevSlide();
      });
    }
    
    // Touch/swipe support
    let startX = 0;
    let startY = 0;
    let isDragging = false;
    
    track.addEventListener('touchstart', (e) => {
      startX = e.touches[0].clientX;
      startY = e.touches[0].clientY;
      isDragging = true;
      this.stopAutoplay();
    });
    
    track.addEventListener('touchmove', (e) => {
      if (!isDragging) return;
      
      const currentX = e.touches[0].clientX;
      const currentY = e.touches[0].clientY;
      const diffX = startX - currentX;
      const diffY = startY - currentY;
      
      // Only handle horizontal swipes
      if (Math.abs(diffX) > Math.abs(diffY)) {
        e.preventDefault();
      }
    });
    
    track.addEventListener('touchend', (e) => {
      if (!isDragging) return;
      
      const endX = e.changedTouches[0].clientX;
      const diffX = startX - endX;
      
      if (Math.abs(diffX) > 50) { // Minimum swipe distance
        if (diffX > 0) {
          nextSlide();
        } else {
          prevSlide();
        }
      }
      
      isDragging = false;
      this.startAutoplay();
    });
    
    // Autoplay
    const startAutoplay = () => {
      stopAutoplay(); // Clear any existing interval
      if (autoplay && slides.length > 1) {
        autoplayInterval = setInterval(() => {
          nextSlide();
        }, autoplaySpeed);
      }
    };
    
    const stopAutoplay = () => {
      if (autoplayInterval) {
        clearInterval(autoplayInterval);
        autoplayInterval = null;
      }
    };
    
    // Pause autoplay on hover (optional - can be removed if you want continuous autoplay)
    slider.addEventListener('mouseenter', stopAutoplay);
    slider.addEventListener('mouseleave', startAutoplay);
    
    // Pause autoplay on touch
    track.addEventListener('touchstart', stopAutoplay);
    track.addEventListener('touchend', () => {
      setTimeout(startAutoplay, 1000); // Resume after 1 second
    });
    
    // Store autoplay functions for external control
    this.startAutoplay = startAutoplay;
    this.stopAutoplay = stopAutoplay;
    
    // Initialize
    updateSlider();
    
    // Start autoplay immediately
    if (autoplay) {
      startAutoplay();
    }
    
    // Update on resize
    window.addEventListener('resize', () => {
      updateSlider();
    });
  }

  /**
   * Get slides per view based on screen size
   */
  getSlidesPerView(desktop, tablet, mobile) {
    if (window.innerWidth <= 768) {
      return mobile;
    } else if (window.innerWidth <= 1024) {
      return tablet;
    } else {
      return desktop;
    }
  }

  /**
   * Create dots navigation
   */
  createDots(container, totalSlides, slidesPerView) {
    container.innerHTML = '';
    const totalDots = Math.ceil(totalSlides / slidesPerView);
    
    for (let i = 0; i < totalDots; i++) {
      const dot = document.createElement('button');
      dot.className = 'urgency-creators__dot';
      dot.setAttribute('aria-label', `Go to slide ${i + 1}`);
      const self = this;
      dot.addEventListener('click', () => {
        const slideIndex = i * slidesPerView;
        if (self.goToSlide) {
          self.goToSlide(slideIndex);
        }
      });
      container.appendChild(dot);
    }
  }

  /**
   * Update slider position
   */
  updateSliderPosition(track, slideIndex, slideWidth) {
    const translateX = -(slideIndex * slideWidth);
    track.style.transform = `translateX(${translateX}%)`;
  }

  /**
   * Update dots active state
   */
  updateDots(container, currentSlide, slidesPerView) {
    if (!container) return;
    
    const dots = container.querySelectorAll('.urgency-creators__dot');
    const activeDotIndex = Math.floor(currentSlide / slidesPerView);
    
    dots.forEach((dot, index) => {
      dot.classList.toggle('urgency-creators__dot--active', index === activeDotIndex);
    });
  }

  /**
   * Update arrow states
   */
  updateArrows(prevBtn, nextBtn, currentSlide, maxSlide) {
    if (prevBtn) {
      prevBtn.style.opacity = currentSlide > 0 ? '1' : '0.5';
      prevBtn.disabled = currentSlide <= 0;
    }
    
    if (nextBtn) {
      nextBtn.style.opacity = currentSlide < maxSlide ? '1' : '0.5';
      nextBtn.disabled = currentSlide >= maxSlide;
    }
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new UrgencyCreators();
  });
} else {
  new UrgencyCreators();
}

// Reinitialize on Shopify section events (for theme editor)
if (window.Shopify && window.Shopify.designMode) {
  document.addEventListener('shopify:section:load', () => {
    new UrgencyCreators();
  });
  
  document.addEventListener('shopify:section:unload', () => {
    // Clean up if needed
  });
}

// Export for external use
window.UrgencyCreators = UrgencyCreators;
